/**
 * Browser-safe Project Catalog keys for D-Tools JSON import.
 * Do not use node:crypto here — this module is bundled for the client.
 */

export const PROJECT_CATALOGUE_KEY_MAX_LEN = 64;

export function normalizeCatalogueName(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function slugToken(v: string | null | undefined): string {
  return String(v ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

/** FNV-1a 32-bit — stable digest for truncating long keys (not cryptographic). */
export function stableKeyDigest(payload: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < payload.length; i++) {
    hash ^= payload.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function clampCatalogueKey(raw: string): string {
  const cleaned = raw.toLowerCase().trim();
  if (cleaned.length <= PROJECT_CATALOGUE_KEY_MAX_LEN) return cleaned;
  const digest = stableKeyDigest(cleaned);
  return `${cleaned.slice(0, PROJECT_CATALOGUE_KEY_MAX_LEN - digest.length - 1)}_${digest}`;
}

export type DtoolsCatalogKeyInput = {
  manufacturer?: string | null;
  model?: string | null;
  catalogId?: string | null;
  componentId?: string | null;
  dtoolsItemId?: string | null;
  displayName?: string | null;
};

/**
 * Stable Project Catalog key for D-Tools imports (canonical writer for new rows).
 * Prefers D-Tools catalogId, then componentId+brand+model, then manufacturer+model slug.
 *
 * Matching existing rows MUST NOT rely on this string alone — use
 * `resolveExistingProjectCatalogItemWithVia` (dtools ids → brand+model → key).
 * Legacy SHA-256 keys from migration 0055 remain valid stored values and are
 * never rewritten on identity match.
 */
export function buildDtoolsProjectCatalogKey(input: DtoolsCatalogKeyInput): string {
  const catalogId = input.catalogId?.trim();
  if (catalogId) {
    return clampCatalogueKey(`dtools-catalog:${slugToken(catalogId)}`);
  }

  const componentId = input.componentId?.trim();
  const brand = slugToken(input.manufacturer);
  const model = slugToken(input.model);
  if (componentId && brand && model) {
    return clampCatalogueKey(
      `dtools-component:${slugToken(componentId)}|${brand}|${model}`,
    );
  }

  const dtoolsItemId = input.dtoolsItemId?.trim();
  if (dtoolsItemId && brand && model) {
    return clampCatalogueKey(`dtools-item:${slugToken(dtoolsItemId)}|${brand}|${model}`);
  }

  // Brand+model identity only — do not bake displayName/description into the key
  // (that caused the same product to mint divergent keys across import paths).
  const identityLabel =
    [input.manufacturer, input.model].filter(Boolean).join(" ") ||
    input.displayName ||
    "item";
  const normalizedName = normalizeCatalogueName(identityLabel);
  return clampCatalogueKey(`dtools-mm:${slugToken(normalizedName)}|${brand}|${model}`);
}
